export const GLOBAL_PAYLOAD_KEY = 'globalPayloadConfig';

/**
 * Recursively collect every key name and its first-seen value from an object.
 * Used to prefill Global Payload Config with all keys present in test case payloads.
 * Keys are matched by name at any depth (same as applyGlobalPayloadToSuites).
 */
function collectKeysFromObject(
  obj: any,
  acc: Record<string, string>,
  seenKeys: Set<string>
): void {
  if (obj === null || obj === undefined) return;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      return;
    }
  }
  if (Array.isArray(obj)) {
    obj.forEach((item) => collectKeysFromObject(item, acc, seenKeys));
    return;
  }
  if (typeof obj !== 'object') return;

  Object.keys(obj).forEach((k) => {
    const val = obj[k];
    if (!seenKeys.has(k)) {
      seenKeys.add(k);
      if (val === null) {
        acc[k] = 'null';
      } else if (typeof val === 'object') {
        acc[k] = JSON.stringify(val);
      } else {
        acc[k] = String(val);
      }
    }
    collectKeysFromObject(val, acc, seenKeys);
  });
}

/**
 * Collect all unique keys (and first-seen values) from every scenario payload/apiRequest.
 * Return Record<key, string> for prefilling the Global Payload Configuration.
 */
export function collectAllKeysFromSuites(suites: any[]): Record<string, string> {
  const acc: Record<string, string> = {};
  const seenKeys = new Set<string>();

  (suites || []).forEach((suite) => {
    (suite.scenarios || []).forEach((sc: any) => {
      const sources = [
        sc.payload,
        sc.apiRequest,
        sc.apiRequest?.body,
        sc.apiRequest?.payload,
      ].filter((s) => s !== undefined && s !== null);
      sources.forEach((source) => collectKeysFromObject(source, acc, seenKeys));
    });
  });

  return acc;
}

export function loadGlobalPayload(): Record<string, string> {
  try {
    const raw = localStorage.getItem(GLOBAL_PAYLOAD_KEY) || '{}';
    return JSON.parse(raw);
  } catch (e) {
    // invalid JSON in localStorage
    // eslint-disable-next-line no-console
    console.warn('invalid global payload in localStorage', e);
    return {};
  }
}

export function saveGlobalPayload(obj: Record<string, string>) {
  try {
    localStorage.setItem(GLOBAL_PAYLOAD_KEY, JSON.stringify(obj || {}));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('failed to save global payload', e);
  }
}

type Suite = any;

export function applyGlobalPayloadToSuites(suites: Suite[], globalPayload: Record<string, string>, opts: { addMissing?: boolean } = { addMissing: false }) {
  if (!globalPayload || Object.keys(globalPayload).length === 0) return suites;
  return suites.map((suite) => {
    const scenarios = (suite.scenarios || []).map((sc: any) => {
      // respect per-scenario override flag
      if (sc && sc.__overrideGlobal) return sc;

      // helper: deep update object/array for matching keys
      const deepUpdate = (obj: any): { updated: any; changed: boolean } => {
        if (Array.isArray(obj)) {
          let changed = false;
          const arr = obj.map((item) => {
            const res = deepUpdate(item);
            if (res.changed) changed = true;
            return res.updated;
          });
          return { updated: arr, changed };
        }

        if (obj && typeof obj === 'object') {
          let changed = false;
          const copy: any = Array.isArray(obj) ? [...obj] : { ...obj };

          // First, recurse into existing keys
          Object.keys(copy).forEach((k) => {
            const val = copy[k];
            if (Object.prototype.hasOwnProperty.call(globalPayload, k)) {
              // replace if different
              if (copy[k] !== globalPayload[k]) {
                copy[k] = globalPayload[k];
                changed = true;
              }
            } else {
              const res = deepUpdate(val);
              if (res.changed) {
                copy[k] = res.updated;
                changed = true;
              }
            }
          });

          // Optionally add missing keys at this level
          if (opts.addMissing) {
            Object.keys(globalPayload).forEach((gk) => {
              if (!Object.prototype.hasOwnProperty.call(copy, gk)) {
                copy[gk] = globalPayload[gk];
                changed = true;
              }
            });
          }

          return { updated: copy, changed };
        }

        // primitives
        return { updated: obj, changed: false };
      };

      // Try common places for payloads:
      // 1) sc.payload (string or object)
      let updatedScenario = { ...sc };
      const tryUpdateField = (fieldPath: string[], source: any) => {
        if (source === undefined || source === null) return false;
        let sourceVal = source;
        let isString = false;
        if (typeof sourceVal === 'string') {
          try {
            sourceVal = JSON.parse(sourceVal);
            isString = true;
          } catch {
            // leave as string
            sourceVal = source;
            isString = false;
          }
        }
        if (typeof sourceVal === 'object' && sourceVal !== null) {
          const res = deepUpdate(sourceVal);
          if (res.changed) {
            const newVal = isString ? JSON.stringify(res.updated) : res.updated;
            // set at fieldPath on updatedScenario
            let objRef: any = updatedScenario;
            for (let i = 0; i < fieldPath.length - 1; i++) {
              const p = fieldPath[i];
              if (!objRef[p]) objRef[p] = {};
              objRef = objRef[p];
            }
            objRef[fieldPath[fieldPath.length - 1]] = newVal;
            return true;
          }
        }
        return false;
      };

      // Track if any updates were made
      let anyUpdated = false;

      // check sc.payload (primary location)
      if (tryUpdateField(['payload'], sc.payload)) {
        anyUpdated = true;
      }
      
      // Also update sc.apiRequest if it exists and has matching keys
      // (some scenarios use apiRequest instead of/in addition to payload)
      if (sc.apiRequest && typeof sc.apiRequest === 'object') {
        // check sc.apiRequest.body
        if (sc.apiRequest.body !== undefined) {
          if (tryUpdateField(['apiRequest', 'body'], sc.apiRequest.body)) {
            anyUpdated = true;
          }
        }
        // check sc.apiRequest.payload
        if (sc.apiRequest.payload !== undefined) {
          if (tryUpdateField(['apiRequest', 'payload'], sc.apiRequest.payload)) {
            anyUpdated = true;
          }
        }
        // check sc.apiRequest directly (if it's the payload itself, not a wrapper)
        // Only if body and payload don't exist
        if (sc.apiRequest.body === undefined && sc.apiRequest.payload === undefined) {
          if (tryUpdateField(['apiRequest'], sc.apiRequest)) {
            anyUpdated = true;
          }
        }
        // check formData
        if (sc.apiRequest.formData && typeof sc.apiRequest.formData === 'object') {
          const fdRes = deepUpdate(sc.apiRequest.formData);
          if (fdRes.changed) {
            updatedScenario.apiRequest = { ...updatedScenario.apiRequest, formData: fdRes.updated };
            anyUpdated = true;
          }
        }
      }

      return anyUpdated ? updatedScenario : sc;
    });
    return { ...suite, scenarios };
  });
}

